import { useEffect, useMemo, useRef } from "react";
import { Plugin, PluginKey } from "prosemirror-state";
import { keymap } from "prosemirror-keymap";
import { baseKeymap } from "prosemirror-commands";
import { EditorState } from "prosemirror-state";
import { EditorView, Decoration, DecorationSet } from "prosemirror-view";
import { sinkListItem, liftListItem, splitListItem } from "prosemirror-schema-list";
import { history } from "prosemirror-history";
import * as chrono from "chrono-node";
import { EditorPanelWorkspaceControls } from "@/components/EditorPanelWorkspaceControls";
import { useEditorContext } from "@/lib/editorContext";
import { editorSchema } from "@/lib/editorSchema";
import { createItemId } from "@/lib/utils";
import "prosemirror-view/style/prosemirror.css";

const PLACEHOLDER = "Start your numbered list... (dates will be detected automatically)";

const autoIdPlugin = new Plugin({
  appendTransaction(transactions, oldState, newState) {
    let tr = newState.tr;
    let needsUpdate = false;
    const seenIds = new Set<string>();

    newState.doc.descendants((node, pos) => {
      if (node.type.name !== "list_item") return;

      const currentId = node.attrs.id as string | undefined;
      if (currentId && !seenIds.has(currentId)) {
        seenIds.add(currentId);
        return;
      }

      needsUpdate = true;
      const nextId = createItemId();
      seenIds.add(nextId);
      tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, id: nextId });
    });

    if (needsUpdate) {
      return tr;
    }

    return null;
  },
});

const dateHighlightPlugin = new Plugin({
  props: {
    decorations(state) {
      const decorations: Decoration[] = [];
      state.doc.descendants((node, pos) => {
        if (node.type.name !== "paragraph") return;
        const text = node.textContent;
        const matches = chrono.parse(text);
        matches.forEach(match => {
          if (!match.text) return;
          const start = pos + 1 + (match.index ?? 0);
          const end = start + match.text.length;
          decorations.push(Decoration.inline(start, end, { class: "date-highlight" }));
        });
      });
      return DecorationSet.create(state.doc, decorations);
    },
  },
});

const collapsePluginKey = new PluginKey<{ collapsed: Set<string> }>("timeline-list-collapse");

const basePlugins = [
  history(),
  keymap({ Enter: splitListItem(editorSchema.nodes.list_item) }),
  keymap({ Tab: sinkListItem(editorSchema.nodes.list_item), "Shift-Tab": liftListItem(editorSchema.nodes.list_item) }),
  keymap(baseKeymap),
  autoIdPlugin,
  dateHighlightPlugin,
];

export function EditorPanel() {
  const { doc, setDocContent, flattenedListItems, selectedItemId } = useEditorContext();
  const editorRootRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const collapsePlugin = useMemo(() => createCollapsePlugin(), []);
  const plugins = useMemo(() => [...basePlugins, collapsePlugin], [collapsePlugin]);

  useEffect(() => {
    if (!editorRootRef.current) return;
    const view = new EditorView(editorRootRef.current, {
      state: EditorState.create({
        doc: editorSchema.nodeFromJSON(doc),
        plugins,
      }),
      dispatchTransaction(transaction) {
        if (!viewRef.current) return;
        const nextState = viewRef.current.state.apply(transaction);
        viewRef.current.updateState(nextState);
        setDocContent(nextState.doc.toJSON());
      },
    });

    view.dom.setAttribute("data-placeholder", PLACEHOLDER);
    view.dom.classList.add("editor-surface");
    view.dom.style.height = "100%";
    view.dom.style.minHeight = "100%";
    view.dom.style.boxSizing = "border-box";
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [plugins, setDocContent]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const currentDoc = view.state.doc.toJSON();
    if (JSON.stringify(currentDoc) === JSON.stringify(doc)) return;
    const updatedState = EditorState.create({ doc: editorSchema.nodeFromJSON(doc), plugins });
    view.updateState(updatedState);
  }, [doc, plugins]);

  useEffect(() => {
    const container = editorRootRef.current;
    if (!container) return;
    const list = Array.from(container.querySelectorAll<HTMLLIElement>("li"));
    list.forEach((node, index) => {
      const item = flattenedListItems[index];
      if (!item) {
        delete node.dataset.hasDate;
        delete node.dataset.dateType;
        delete node.dataset.dateRange;
        delete node.dataset.itemId;
        delete node.dataset.selected;
        return;
      }
      const hasAutoDate = Boolean(item.extractedStartDate);
      const hasManualDate = Boolean(item.manualStartDate);
      const hasRange = hasManualDate ? Boolean(item.manualEndDate) : Boolean(item.extractedEndDate);
      node.dataset.hasDate = (hasAutoDate || hasManualDate).toString();
      node.dataset.dateType = hasManualDate ? "manual" : hasAutoDate ? "auto" : "none";
      node.dataset.dateRange = hasRange ? "true" : "false";
      node.dataset.itemId = item.id;
      node.dataset.selected = item.id === selectedItemId ? "true" : "false";
    });
  }, [flattenedListItems, selectedItemId]);

  return (
    <div className="flex h-full w-full min-h-0 flex-1 flex-col gap-3 rounded-2xl p-4">
      <EditorPanelWorkspaceControls />
      <div className="flex-1 min-h-0">
        <div ref={editorRootRef} className="h-full min-h-60 w-full rounded-xl border border-dashed border-border/60 p-3" />
      </div>
    </div>
  );
}

function createCollapsePlugin() {
  type CollapseState = { collapsed: Set<string> };
  return new Plugin<CollapseState>({
    key: collapsePluginKey,
    state: {
      init() {
        return { collapsed: new Set<string>() };
      },
      apply(tr, value, _oldState, newState) {
        let collapsed = value.collapsed;
        let changed = false;
        const meta = tr.getMeta(collapsePluginKey) as { toggleId?: string } | undefined;
        if (meta?.toggleId) {
          const next = new Set(collapsed);
          if (next.has(meta.toggleId)) {
            next.delete(meta.toggleId);
          } else {
            next.add(meta.toggleId);
          }
          collapsed = next;
          changed = true;
        }

        if (tr.docChanged) {
          const survivingIds = new Set<string>();
          newState.doc.descendants(node => {
            if (node.type.name === "list_item" && typeof node.attrs.id === "string") {
              survivingIds.add(node.attrs.id);
            }
          });
          const next = new Set<string>();
          collapsed.forEach(id => {
            if (survivingIds.has(id)) {
              next.add(id);
            } else {
              changed = true;
            }
          });
          collapsed = next;
        }

        return changed ? { collapsed } : value;
      },
    },
    props: {
      decorations(state) {
        const collapseState = collapsePluginKey.getState(state);
        const collapsedIds = collapseState?.collapsed ?? new Set<string>();
        const decorations: Decoration[] = [];
        state.doc.descendants((node, pos) => {
          if (node.type.name !== "list_item") return;
          const itemId = typeof node.attrs.id === "string" ? node.attrs.id : null;
          if (!itemId) return;
          let hasChildren = false;
          node.forEach(child => {
            if (child.type.name === "ordered_list") {
              hasChildren = true;
            }
          });
          const isCollapsed = hasChildren && collapsedIds.has(itemId);
          const attrs: Record<string, string> = {
            "data-item-id": itemId,
            "data-has-children": hasChildren ? "true" : "false",
            "data-collapsed": isCollapsed ? "true" : "false",
          };
          decorations.push(Decoration.node(pos, pos + node.nodeSize, attrs));

          if (hasChildren) {
            const labelText = node.textContent?.trim() || "list item";
            decorations.push(
              Decoration.widget(
                pos + 1,
                () => {
                  const button = document.createElement("button");
                  button.type = "button";
                  button.className = "editor-collapse-toggle";
                  button.setAttribute("data-collapse-toggle", "true");
                  button.setAttribute("aria-expanded", (!isCollapsed).toString());
                  button.setAttribute(
                    "aria-label",
                    `${isCollapsed ? "Expand" : "Collapse"} nested items for ${labelText}`
                  );
                  button.textContent = isCollapsed ? "▸" : "▾";
                  button.tabIndex = -1;
                  button.contentEditable = "false";
                  return button;
                },
                { ignoreSelection: true, side: -1, key: `collapse-${itemId}` }
              )
            );
          }
        });
        return DecorationSet.create(state.doc, decorations);
      },
      handleDOMEvents: {
        mousedown(view, event) {
          const target = event.target as HTMLElement | null;
          if (!target) return false;
          const toggle = target.closest<HTMLElement>("[data-collapse-toggle]");
          if (!toggle) return false;
          const listItem = toggle.closest<HTMLElement>("li[data-item-id]");
          const itemId = listItem?.getAttribute("data-item-id");
          if (!itemId) return false;
          event.preventDefault();
          const tr = view.state.tr.setMeta(collapsePluginKey, { toggleId: itemId });
          view.dispatch(tr);
          return true;
        },
      },
    },
  });
}
